/**
 * Edit one member's usage limits: the included monthly allowance (Amoreg's
 * to set — operators only, the API refuses anyone else) and whether they may
 * keep going past it on demand (the organization admin's switch). One small
 * dialog with validation instead of inputs strewn down the page.
 *
 * @module components/settings/MemberAllowanceDialog
 */
import { useState, type ReactNode } from "react";

import type { OrgTeamMember } from "../../state/orgProfile";
import { formatGbp } from "../../usage/usageFormat";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "../ui/input-group";
import { Radio, RadioGroup } from "../ui/radio-group";
import { Switch } from "../ui/switch";
import { memberDisplayName, parseCustomAllowance } from "./orgMembers.logic";

export interface MemberAllowanceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly member: OrgTeamMember;
  /** The platform's standard allowance when known. */
  readonly standardGbp: number | null;
  /** The on-demand rate as a multiple of standard, when known. */
  readonly overageMultiplier: number | null;
  /** Organization admin: may switch on-demand usage. */
  readonly canManage: boolean;
  /** Amoreg operator: may set the allowance itself. */
  readonly isOperator: boolean;
  readonly onToggleOnDemand: (userId: string, enabled: boolean) => Promise<string | null>;
  readonly onSetAllowance: (userId: string, allowanceGbp: number | null) => Promise<string | null>;
}

type AllowanceMode = "standard" | "custom";

export function MemberAllowanceDialog(props: MemberAllowanceDialogProps) {
  const { open, onOpenChange } = props;
  // A fresh form per opening, so it always starts from the saved values —
  // without blanking the popup while it animates closed.
  const [lastOpen, setLastOpen] = useState(open);
  const [session, setSession] = useState(0);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) setSession((value) => value + 1);
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-md">
        <AllowanceForm key={`${props.member.id}:${session}`} {...props} />
      </DialogPopup>
    </Dialog>
  );
}

function AllowanceForm({
  onOpenChange,
  member,
  standardGbp,
  overageMultiplier,
  canManage,
  isOperator,
  onToggleOnDemand,
  onSetAllowance,
}: MemberAllowanceDialogProps) {
  const [mode, setMode] = useState<AllowanceMode>(
    member.modelAllowanceGbp === null ? "standard" : "custom",
  );
  const [amount, setAmount] = useState(
    member.modelAllowanceGbp === null ? "" : member.modelAllowanceGbp.toFixed(2),
  );
  const [onDemand, setOnDemand] = useState(member.onDemandModelUsage);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsed: { ok: true; value: number | null } | { ok: false; reason: string } =
    mode === "standard" ? { ok: true, value: null } : parseCustomAllowance(amount);
  const allowanceChanged = isOperator && parsed.ok && parsed.value !== member.modelAllowanceGbp;
  const onDemandChanged = canManage && onDemand !== member.onDemandModelUsage;
  const canSave = !isSaving && parsed.ok && (allowanceChanged || onDemandChanged);
  const name = memberDisplayName(member);
  const rate = overageMultiplier === null ? "a higher rate" : `${Number.parseFloat(overageMultiplier.toFixed(2))}× the standard rate`;

  const save = async () => {
    if (!canSave || !parsed.ok) return;
    setIsSaving(true);
    setError(null);
    if (allowanceChanged) {
      const failure = await onSetAllowance(member.id, parsed.value);
      if (failure) {
        setIsSaving(false);
        setError(failure);
        return;
      }
    }
    if (onDemandChanged) {
      const failure = await onToggleOnDemand(member.id, onDemand);
      if (failure) {
        setIsSaving(false);
        setError(failure);
        return;
      }
    }
    setIsSaving(false);
    onOpenChange(false);
  };

  return (
    <form
      className="flex min-h-0 flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <DialogHeader>
        <DialogTitle>Usage limits</DialogTitle>
        <DialogDescription>{name} · resets on the 1st of each month</DialogDescription>
      </DialogHeader>
      <DialogPanel className="space-y-6">
        <fieldset className="space-y-2.5" disabled={isSaving}>
          <legend className="mb-2.5 flex w-full items-center justify-between text-sm font-medium">
            Monthly allowance
            {!isOperator ? (
              <span className="text-xs font-normal text-muted-foreground">Set by Amoreg</span>
            ) : null}
          </legend>
          {isOperator ? (
            <RadioGroup
              value={mode}
              onValueChange={(value) => {
                setMode(value as AllowanceMode);
                setError(null);
              }}
              aria-label="Monthly allowance"
              className="gap-1.5"
            >
              <AllowanceOption
                value="standard"
                title="Standard"
                hint={
                  standardGbp === null
                    ? "The platform default, set by Amoreg."
                    : `${formatGbp(standardGbp)} a month, the platform default.`
                }
              />
              <AllowanceOption value="custom" title="Custom amount" hint="A monthly figure for this member.">
                {mode === "custom" ? (
                  <div className="mt-2.5 space-y-1.5">
                    <InputGroup className="w-52">
                      <InputGroupAddon>
                        <InputGroupText>£</InputGroupText>
                      </InputGroupAddon>
                      <InputGroupInput
                        autoFocus
                        inputMode="decimal"
                        value={amount}
                        placeholder="120.00"
                        aria-label={`Monthly allowance for ${member.email}, in pounds`}
                        aria-invalid={!parsed.ok || undefined}
                        className="tabular-nums"
                        onChange={(event) => {
                          setAmount(event.currentTarget.value);
                          setError(null);
                        }}
                      />
                      <InputGroupAddon align="inline-end">
                        <InputGroupText className="whitespace-nowrap">a month</InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                    {!parsed.ok && amount.trim().length > 0 ? (
                      <p className="text-xs text-destructive">{parsed.reason}</p>
                    ) : null}
                  </div>
                ) : null}
              </AllowanceOption>
            </RadioGroup>
          ) : (
            <div className="rounded-lg border border-border/70 px-3 py-2.5">
              <p className="text-sm text-foreground tabular-nums">
                {member.modelAllowanceGbp !== null
                  ? `${formatGbp(member.modelAllowanceGbp)} a month`
                  : standardGbp !== null
                    ? `${formatGbp(standardGbp)} a month · standard`
                    : "Standard allowance"}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Ask Amoreg to change a member's included allowance.
              </p>
            </div>
          )}
        </fieldset>

        <label className="flex items-start justify-between gap-4 rounded-lg border border-border/70 px-3 py-2.5">
          <span className="min-w-0">
            <span className="block text-sm font-medium">On-demand usage</span>
            <span className="mt-0.5 block text-xs leading-4 text-muted-foreground">
              {onDemand
                ? `Past the allowance, usage continues and is billed at ${rate}.`
                : "Conversations pause once the allowance is used, until it resets."}
              {!canManage ? " Only organization admins can change this." : ""}
            </span>
          </span>
          <Switch
            aria-label={`Allow on-demand model usage for ${member.email}`}
            checked={onDemand}
            disabled={!canManage || isSaving}
            onCheckedChange={(enabled) => {
              setOnDemand(enabled);
              setError(null);
            }}
            className="mt-0.5"
          />
        </label>

        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </DialogPanel>
      <DialogFooter>
        <Button type="button" variant="ghost" disabled={isSaving} onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSave}>
          {isSaving ? "Saving…" : "Save"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function AllowanceOption({
  value,
  title,
  hint,
  children,
}: {
  readonly value: AllowanceMode;
  readonly title: string;
  readonly hint: string;
  readonly children?: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-border/70 px-3 py-2.5 transition-colors hover:bg-muted/40 has-data-checked:border-primary/40 has-data-checked:bg-primary/[0.04]">
      <Radio value={value} className="mt-0.5" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="mt-0.5 block text-xs leading-4 text-muted-foreground">{hint}</span>
        {children}
      </span>
    </label>
  );
}
